/**
 * Maps a route pathname to the canonical `screen_name` (+ `screen_class`)
 * GA4 should record for `screen_view`, shared with the mobile app.
 *
 * Source: dofarm_obsidian_vault/conventions/analytics-tracking-plan.md
 * (DOF-1069). A few routes aren't in that ~30-screen registry yet (e.g. the
 * inventory sub-pages, iot sub-pages) — named consistently with its
 * conventions; reconcile with the vault doc when convenient.
 *
 * Patterns use react-router-style `:param` segments, matched via a simple
 * per-segment comparison (a `:param` entry matches any single path segment)
 * — no router dependency needed since the route list is finite and known.
 */

interface ScreenNameEntry {
  pattern: string
  screenName: string
  screenClass: string
}

const REGISTRY: ScreenNameEntry[] = [
  { pattern: '/policy-consent', screenName: 'policy_consent', screenClass: 'PolicyConsentPage' },
  { pattern: '/settings', screenName: 'settings', screenClass: 'SettingsMainPage' },
  { pattern: '/shrimp-price', screenName: 'shrimp_prices', screenClass: 'ShrimpPricePage' },
  { pattern: '/auth/login', screenName: 'login', screenClass: 'Login' },
  { pattern: '/auth/register', screenName: 'register', screenClass: 'Register' },
  { pattern: '/auth/reset-password', screenName: 'forgot_password', screenClass: 'ForgetPassword' },

  { pattern: '/dashboard/farms', screenName: 'farms_list', screenClass: 'AllFarmsPage' },
  { pattern: '/dashboard/farms/:farmId', screenName: 'ponds_list', screenClass: 'AllPondsPage' },
  {
    pattern: '/dashboard/farms/:farmId/pond/:pondId/dashboard',
    screenName: 'pond_overview',
    screenClass: 'OverviewDashboard',
  },
  {
    pattern: '/dashboard/farms/:farmId/pond/:pondId/setting',
    screenName: 'pond_setting',
    screenClass: 'SettingPage',
  },
  {
    pattern: '/dashboard/farms/:farmId/pond/:pondId/food',
    screenName: 'pond_feed',
    screenClass: 'Food',
  },
  {
    pattern: '/dashboard/farms/:farmId/pond/:pondId/growth',
    screenName: 'pond_growth',
    screenClass: 'GrowthShrimp',
  },
  {
    pattern: '/dashboard/farms/:farmId/pond/:pondId/water',
    screenName: 'pond_water_quality',
    screenClass: 'Water',
  },
  {
    pattern: '/dashboard/farms/:farmId/pond/:pondId/dead-shrimp',
    screenName: 'pond_mortality',
    screenClass: 'DeadShrimp',
  },
  {
    pattern: '/dashboard/farms/:farmId/pond/:pondId/medicine',
    screenName: 'pond_medicine',
    screenClass: 'MedicalSupply',
  },
  {
    pattern: '/dashboard/farms/:farmId/pond/:pondId/cost',
    screenName: 'pond_cost',
    screenClass: 'Cost',
  },
  {
    pattern: '/dashboard/farms/:farmId/pond/:pondId/sales',
    screenName: 'pond_shrimp_sales',
    screenClass: 'ShrimpSales',
  },
  {
    pattern: '/dashboard/farms/:farmId/pond/:pondId/transfer',
    screenName: 'pond_shrimp_transfer',
    screenClass: 'ShrimpTransfer',
  },
  {
    pattern: '/dashboard/farms/:farmId/pond/:pondId/topup',
    screenName: 'pond_shrimp_stocking',
    screenClass: 'ShrimpTopup',
  },

  {
    pattern: '/dashboard/comparison',
    screenName: 'pond_comparison',
    screenClass: 'ComparisonPage',
  },
  {
    pattern: '/dashboard/organization',
    screenName: 'organization_overview',
    screenClass: 'OrganizationOverviewPage',
  },
  { pattern: '/dashboard/members', screenName: 'members', screenClass: 'ManagementPage' },
  {
    pattern: '/dashboard/members/roles',
    screenName: 'member_roles',
    screenClass: 'ManagementPage',
  },
  { pattern: '/dashboard/profile', screenName: 'profile', screenClass: 'ProfilePage' },
  { pattern: '/dashboard/settings', screenName: 'settings_app', screenClass: 'AppSettingsPage' },
  { pattern: '/dashboard/feedback', screenName: 'feedback', screenClass: 'FeedbackPage' },
  {
    pattern: '/dashboard/change-phone',
    screenName: 'settings_change_phone',
    screenClass: 'ChangePhonePage',
  },
  {
    pattern: '/dashboard/shrimp-price',
    screenName: 'shrimp_prices',
    screenClass: 'ShrimpPricePage',
  },

  { pattern: '/dashboard/category', screenName: 'inventory_category', screenClass: 'CategoryPage' },
  { pattern: '/dashboard/product', screenName: 'inventory_products', screenClass: 'ProductPage' },
  {
    pattern: '/dashboard/product/:id',
    screenName: 'inventory_product_detail',
    screenClass: 'ProductDetail',
  },
  {
    pattern: '/dashboard/product/:id/history',
    screenName: 'inventory_product_history',
    screenClass: 'ProductHistory',
  },
  {
    pattern: '/dashboard/warehouse',
    screenName: 'inventory_warehouse',
    screenClass: 'WarehousePage',
  },
  { pattern: '/dashboard/stock', screenName: 'inventory_stock', screenClass: 'StockPage' },
  {
    pattern: '/dashboard/inventory/settings',
    screenName: 'inventory_settings',
    screenClass: 'InventorySettingsPage',
  },
  {
    pattern: '/dashboard/inventory/settings/display/:featureCode',
    screenName: 'inventory_display_settings',
    screenClass: 'InventoryDisplaySettingsPage',
  },

  {
    pattern: '/dashboard/notifications',
    screenName: 'notifications',
    screenClass: 'NotificationPage',
  },
  {
    pattern: '/dashboard/notifications/settings',
    screenName: 'notification_settings',
    screenClass: 'NotificationSettingPage',
  },
  {
    pattern: '/dashboard/policy',
    screenName: 'policy_document',
    screenClass: 'PolicyDocumentPage',
  },
  { pattern: '/dashboard/wallet', screenName: 'credit_wallet', screenClass: 'WalletPage' },

  { pattern: '/dashboard/iot', screenName: 'iot_devices', screenClass: 'IotPage' },
  { pattern: '/dashboard/iot/new', screenName: 'iot_device_create', screenClass: 'IotCreatePage' },
  {
    pattern: '/dashboard/iot/:deviceId',
    screenName: 'iot_device_detail',
    screenClass: 'IotDetailPage',
  },
  {
    pattern: '/dashboard/iot/:deviceId/setting',
    screenName: 'iot_device_setting',
    screenClass: 'IotSettingPage',
  },
  {
    pattern: '/dashboard/iot/:deviceId/threshold',
    screenName: 'iot_device_threshold',
    screenClass: 'IotThresholdPage',
  },
]

const patternMatches = (pattern: string, pathname: string): boolean => {
  const patternSegments = pattern.split('/').filter(Boolean)
  const pathSegments = pathname.split('/').filter(Boolean)
  if (patternSegments.length !== pathSegments.length) return false
  return patternSegments.every(
    (segment, i) => segment.startsWith(':') || segment === pathSegments[i]
  )
}

export function getScreenName(
  pathname: string
): { screenName: string; screenClass: string } | undefined {
  const match = REGISTRY.find((entry) => patternMatches(entry.pattern, pathname))
  return match ? { screenName: match.screenName, screenClass: match.screenClass } : undefined
}
